/**
 * «این منبع را همین حالا امتحان کن» — شش قدم، با خروجیِ هر قدم.
 *
 * ═══ چرا این‌جا و نه در سربرگِ کرالر ═══
 *
 * سربرگِ کرالر می‌گوید **دیشب چه شد**؛ این‌جا می‌گوید **الان چه می‌شود**.
 * سوالِ دوم فقط وقتی پرسیده می‌شود که آدم دارد تنظیماتِ یک منبع را درست
 * می‌کند، و جوابش باید کنارِ همان فرم باشد نه یک سربرگ آن‌طرف‌تر.
 *
 * ⚠️ **این پنل نتیجه را نمی‌سازد، فقط نشان می‌دهد.** خودِ تست در کرالر
 * اجرا می‌شود (مرورگر نه می‌تواند صفحه‌ی سایتِ دیگری را بگیرد، نه
 * detector و extractor را دارد). پس دکمه یک کار در صف می‌گذارد و این‌جا
 * دنبالش می‌کند — و اگر `crawl jobs --watch` روشن نباشد، همان‌جا در صف
 * می‌ماند. متنِ حالتِ انتظار همین را می‌گوید، چون اولین باری که کسی
 * منتظر بماند و چیزی نیاید، فکر می‌کند ابزار خراب است.
 *
 * ⚠️ **حکمِ هر قدم این‌جا حساب نمی‌شود** بلکه در
 * `core/lib/sourceTest.verdicts` — همان‌جا هم تست دارد. این فایل فقط
 * رنگ و چیدمان است.
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sourceRepo, type SourceTestRunDetail } from '@/core/data/sourceRepo';
import {
  overall, readReport, verdicts, factsOf, needOf, NEED_KIND_LABEL,
  humanBytes, PAGE_ROLE_LABEL, STATUS_ICON, STEP_LABEL, STEP_ORDER,
  type FetchFacts, type FieldSample, type ItemSample,
  type SourceTestPage, type SourceTestReport, type SourceTestRun, type StepKey,
} from '@/core/lib/sourceTest';
import { reportError } from '@/core/lib/report';
import { Modal } from '@/ui/Modal';

const when = (iso: string | null) =>
  (iso ? new Date(iso).toLocaleString('fa-IR', { hour12: false }) : '—');

const RUN_LABEL: Record<SourceTestRun['settings_from'], string> = {
  registry: 'تنظیماتِ ثبت‌شده',
  draft: 'حدس (پیش‌نویس)',
  form: 'تنظیماتِ ثبت‌نشده',
};

/** یک ردیفِ «برچسب: مقدار» — همه‌ی جدول‌های ریزِ این پنل از این ساخته شده‌اند. */
function Facts({ rows }: { rows: [string, React.ReactNode][] }) {
  return (
    <table className="tbl">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <td className="dim" style={{ width: 130 }}>{k}</td>
            <td>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** واقعیت‌های یک درخواستِ شبکه، با دکمه‌ی دیدنِ خودِ صفحه. */
function FetchBlock({ f, onOpen }: { f: FetchFacts; onOpen: (id: number) => void }) {
  return (
    <>
      <Facts rows={[
        ['آدرس', <a className="mono" href={f.url} target="_blank" rel="noreferrer">{f.url}</a>],
        ...(f.final_url && f.final_url !== f.url
          ? [['بعد از ریدایرکت', <span className="mono">{f.final_url}</span>] as [string, React.ReactNode]]
          : []),
        ['نتیجه', <span className={f.outcome === 'fetched' ? '' : 'bad'}>{f.outcome}</span>],
        ['کدِ HTTP', <span className="num">{f.http_status ?? '—'}</span>],
        ['حجم', <span className="num">{humanBytes(f.bytes)}</span>],
        ['زمان', <span className="num">{f.duration_ms} میلی‌ثانیه</span>],
        ['نوعِ محتوا', <span className="mono">{f.content_type ?? '—'}</span>],
        ['robots.txt', f.robots_allowed == null
          ? <span className="dim">پرسیده نشد — درخواستی فرستاده نشد</span>
          : (f.robots_allowed ? 'اجازه داد' : <span className="bad">بست</span>)],
        ['دیوارِ ضدربات', f.bot_wall
          ? <span className="bad mono">{f.bot_wall}</span>
          : <span className="dim">نشانه‌ای نبود</span>],
        ...(f.detail ? [['جزئیات', <span className="mono">{f.detail}</span>] as [string, React.ReactNode]] : []),
      ]} />
      {f.page_id != null ? (
        <button className="btn ghost" type="button" style={{ marginTop: 6 }}
          onClick={() => onOpen(f.page_id!)}>دیدنِ صفحه</button>
      ) : null}
    </>
  );
}

/** یک آیتمِ پیداشده — قطعه‌کدِ خامش، که مورد ۳ خواسته بود. */
function ItemBlock({ s }: { s: ItemSample }) {
  return (
    <div style={{ marginTop: 8 }}>
      <p className="dim" style={{ fontSize: 12 }}>
        #{s.ordinal} · <span className="mono">{s.key}</span>
        {s.locator ? <> · جایگاه <span className="mono">{s.locator}</span></> : null}
      </p>
      <pre className="raw-json">{s.snippet}{s.snippet_truncated ? '\n…' : ''}</pre>
      {s.links.length > 0 ? (
        <p className="dim" style={{ fontSize: 12 }}>
          لینک‌ها: {s.links.slice(0, 6).map((l) => (
            <a key={l} className="mono" href={l} target="_blank" rel="noreferrer"
              style={{ marginInlineEnd: 6 }}>{l}</a>
          ))}
          {s.links.length > 6 ? <span>و {s.links.length - 6} تای دیگر</span> : null}
        </p>
      ) : null}
    </div>
  );
}

/** فیلدهای یک آیتم. مقدارِ غیرِمتنی JSON نشان داده می‌شود تا چیزی گم نشود. */
function FieldBlock({ s }: { s: FieldSample }) {
  return (
    <div style={{ marginTop: 8 }}>
      <p className="dim" style={{ fontSize: 12 }}>
        {s.page === 'detail' ? 'از صفحه‌ی رخداد' : `از صفحه‌ی ${s.page}`} ·{' '}
        <span className="mono">{s.item_key}</span>
      </p>
      <table className="tbl">
        <thead><tr><th>فیلد</th><th>مقدار</th><th>از</th></tr></thead>
        <tbody>
          {s.fields.map((f, i) => (
            <tr key={`${f.field}-${i}`}>
              <td className="mono">{f.field}</td>
              <td className={f.value === null || f.value === '' ? 'dim' : ''}>
                {typeof f.value === 'string'
                  ? f.value
                  : <span className="mono">{JSON.stringify(f.value)}</span>}
              </td>
              <td className="dim mono">
                {f.extractor}{f.confidence != null ? ` · ${f.confidence}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * بدنه‌ی یک صفحه، خام و رندرشده.
 *
 * ⚠️ `sandbox=""` **همه‌چیز** را می‌بندد — اسکریپت، فرم، ناوبری، دسترسی
 * به این صفحه. یعنی رندر زشت است (CSS و تصویرِ سایت لود نمی‌شود) و همین
 * هم درست است: سوالِ این کادر «سایت چه شکلی است؟» نیست، «آیا واقعاً
 * محتوا آمده یا این صفحه‌ی کپچاست؟» است.
 */
function PageViewer({ id, onClose }: { id: number; onClose: () => void }) {
  const [raw, setRaw] = useState(true);
  const page = useQuery({
    queryKey: ['srcTestPage', id],
    queryFn: () => sourceRepo.testPage(id),
  });

  const p = page.data;
  return (
    <Modal wide title={p ? PAGE_ROLE_LABEL[p.role] : 'صفحه'}
      hint={p ? `${humanBytes(p.bytes)} · HTTP ${p.http_status ?? '—'}`
        + `${p.truncated ? ' · بدنه برای نمایش بریده شده' : ''}` : undefined}
      onClose={onClose}>
      {page.isLoading ? <p className="dim">…</p> : null}
      {page.isError ? <p className="err">{(page.error as Error)?.message}</p> : null}
      {p ? (
        <>
          <p className="mono" style={{ fontSize: 12 }}>{p.url}</p>
          <div className="chiprow" style={{ margin: '8px 0' }}>
            <button className={raw ? 'chip on' : 'chip'} type="button"
              onClick={() => setRaw(true)}>خام</button>
            <button className={raw ? 'chip' : 'chip on'} type="button"
              onClick={() => setRaw(false)}>رندرشده</button>
          </div>
          {raw
            ? <pre className="raw-json testpage">{p.body ?? ''}</pre>
            : <iframe className="testframe" sandbox="" title="صفحه" srcDoc={p.body ?? ''} />}
        </>
      ) : null}
    </Modal>
  );
}

/**
 * مسابقه‌ی خواننده‌ها — همه‌ی عمومی‌ها روی همان بایت‌ها.
 *
 * ⚠️ ستونِ تصمیم‌ساز **«عنوان + زمان»** است، نه «فیلد». خواننده‌ای که از
 * شصت بلوکِ ناوبری شصت «عنوان» دربیاورد با شمارشِ خام برنده می‌شود و
 * منبع را با شصت رخدادِ بی‌تاریخ پر می‌کند.
 *
 * ⚠️ و ثبت **یک کلیکِ آدم** است، نه خودکار. عوض کردنِ `extractor` فیلدِ
 * رفتاری است و نسخه‌ی تازه می‌سازد؛ تستی که خودش بنویسد، آن قولِ اصلی را
 * می‌شکند که باعث می‌شود بشود بیست بار پشتِ هم تست زد. ضمناً خودِ سنجه
 * هم می‌تواند غلط باشد — ۲۴ اوت سه باگ داشت و «ثبتِ خودکار» بیست منبعِ
 * سالم را جابه‌جا می‌کرد.
 */
function Bakeoff({ step, onApply, busy }: {
  step: NonNullable<SourceTestReport['steps']['extract']>;
  onApply: (name: string) => void;
  busy: boolean;
}) {
  if (!step.tried?.length) return null;
  return (
    <>
      <h3 style={{ marginTop: 10 }}>مسابقه‌ی خواننده‌ها</h3>
      <table className="tbl">
        <thead>
          <tr>
            <th>خواننده</th>
            <th className="num">عنوان + زمان</th>
            <th className="num">هر فیلدی</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {step.tried.map((t) => (
            <tr key={t.extractor}>
              <td className="mono">
                {t.extractor}{t.current ? ' ← فعلی' : ''}
                {t.extractor === step.best ? ' ✅' : ''}
              </td>
              <td className={`num${t.with_essential > 0 ? '' : ' dim'}`}>
                {t.with_essential}
              </td>
              <td className="num dim">{t.with_fields}</td>
              <td>
                {t.extractor === step.best ? (
                  <button className="btn ghost" type="button" disabled={busy}
                    onClick={() => onApply(t.extractor)}>این را ثبت کن</button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {step.best ? (
        <p className="note">
          «{step.best}» روی این صفحه {step.best_essential} رخدادِ کامل می‌دهد و
          تنظیمِ فعلی هیچ. ثبتش یک نسخه‌ی تازه در رجیستری می‌سازد.
        </p>
      ) : null}
    </>
  );
}

export function SourceTestPanel({ target, autoStart, settings, onQueued }: {
  target: string;
  /** با باز شدنِ پنل یک تست ثبت کن — دکمه‌ی «تست»ِ ردیفِ جدول این را می‌دهد. */
  autoStart?: boolean;
  /** تنظیماتِ ثبت‌نشده، وقتی تست از فرمِ ویرایش آمده باشد. */
  settings?: Record<string, unknown> | null;
  onQueued?: (message: string) => void;
}) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<number | null>(null);
  const [openStep, setOpenStep] = useState<StepKey | null>(null);
  const [openPage, setOpenPage] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);

  const runs = useQuery({
    queryKey: ['srcTestRuns', target],
    queryFn: () => sourceRepo.testRuns(target),
    // ⚠️ فقط وقتی چیزی در جریان است. بدونِ این شرط، هر ردیفِ بازِ جدول
    // برای همیشه هر سه ثانیه یک کوئری می‌زند — و ادمین معمولاً چند ردیف
    // باز دارد.
    refetchInterval: (q) => {
      const list = q.state.data as SourceTestRun[] | undefined;
      return list?.some((r) => r.status === 'queued' || r.status === 'running')
        ? 3000 : false;
    },
  });

  const list = runs.data ?? [];
  const current = list.find((r) => r.id === picked) ?? list[0] ?? null;

  const detail = useQuery({
    queryKey: ['srcTestRun', current?.id, current?.status],
    queryFn: () => sourceRepo.testRun(current!.id),
    enabled: current != null && current.status === 'done',
  });

  const start = useMutation({
    mutationFn: () => sourceRepo.test(target, settings ?? null),
    onSuccess: (run) => {
      setPicked(run.id);
      setError(null);
      qc.invalidateQueries({ queryKey: ['srcTestRuns', target] });
      onQueued?.(`تستِ ${target} در صف نشست.`);
    },
    onError: (err) => {
      reportError('admin.sources.test', err);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  /**
   * ثبتِ برنده‌ی مسابقه.
   *
   * ⚠️ برای منبعِ رجیستری از همان صفِ `edit_source` رد می‌شود (فیلدِ
   * رفتاری است و نسخه می‌سازد)، و برای پیش‌نویس فوری می‌نشیند. تشخیصِ
   * این دو از روی پیشوندِ `draft:` است، مثلِ بقیه‌ی جاها.
   */
  const apply = useMutation({
    mutationFn: async (name: string): Promise<void> => {
      if (target.startsWith('draft:')) await sourceRepo.editDraft(target, { extractor: name });
      else await sourceRepo.queue('edit_source', target, { extractor: name });
    },
    onSuccess: (_r, name) => {
      qc.invalidateQueries({ queryKey: ['srcPipeline'] });
      qc.invalidateQueries({ queryKey: ['srcDetail'] });
      onQueued?.(target.startsWith('draft:')
        ? `خواننده‌ی ${target} شد «${name}».`
        : `عوض کردنِ خواننده به «${name}» در صف نشست.`);
    },
    onError: (err) => {
      reportError('admin.sources.applyExtractor', err);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => sourceRepo.testDelete(id),
    onSuccess: () => {
      setPicked(null);
      qc.invalidateQueries({ queryKey: ['srcTestRuns', target] });
    },
    onError: (err) => reportError('admin.sources.testDelete', err),
  });

  // یک بار، و فقط وقتی پنل با درخواستِ صریح باز شده.
  useEffect(() => {
    if (autoStart && !started) {
      setStarted(true);
      start.mutate();
    }
  }, [autoStart, started]); // eslint-disable-line react-hooks/exhaustive-deps

  const d: SourceTestRunDetail | undefined = detail.data;
  const report = readReport(d?.run.report);
  const marks = verdicts(report);
  const sum = overall(report);
  const pages: SourceTestPage[] = d?.pages ?? [];
  const steps = report?.steps ?? {};

  const openPageBy = (id: number) => setOpenPage(id);

  return (
    <div>
      <div className="chiprow" style={{ marginBottom: 8 }}>
        <button className="btn" type="button" disabled={start.isPending}
          onClick={() => { setError(null); start.mutate(); }}>
          {start.isPending ? '…' : (settings ? 'با همین تنظیمات تست کن' : 'تست کن')}
        </button>
        {list.map((r) => (
          <button key={r.id} type="button"
            className={r.id === current?.id ? 'chip on' : 'chip'}
            onClick={() => { setPicked(r.id); setOpenStep(null); }}>
            {when(r.requested_at)} · {RUN_LABEL[r.settings_from]}
            {r.status === 'done' ? '' : ` · ${r.status}`}
          </button>
        ))}
        {current ? (
          <button className="linkbtn" type="button" disabled={remove.isPending}
            onClick={() => remove.mutate(current.id)}>حذفِ این اجرا</button>
        ) : null}
      </div>

      {error ? <p className="err">{error}</p> : null}

      {!current ? (
        <p className="dim">هنوز تستی برای این منبع اجرا نشده.</p>
      ) : null}

      {/*
        ⚠️ متنِ انتظار عمداً می‌گوید کار در **کجا** منتظر است. تست در
        کرالر اجرا می‌شود، و اگر آن حلقه روشن نباشد این ردیف تا اجرای
        شبانه در صف می‌ماند — بدونِ این جمله، آدم فکر می‌کند ابزار خراب
        است و دوباره و دوباره کلیک می‌کند.
      */}
      {current && (current.status === 'queued' || current.status === 'running') ? (
        <p className="note">
          {current.status === 'queued' ? 'در صف نشسته' : 'در حالِ اجرا'} —
          {' '}کرالر برش می‌دارد. اگر بیشتر از یکی-دو دقیقه ماند، یعنی
          {' '}<span className="mono">crawl jobs --watch</span> روشن نیست؛
          {' '}تا وقتی روشن نشود این کار تا اجرای شبانه منتظر می‌ماند.
        </p>
      ) : null}

      {current?.status === 'failed' ? (
        <p className="err">اجرا شکست خورد: {current.error ?? 'بی‌جزئیات'}</p>
      ) : null}

      {detail.isError ? (
        <p className="err">{(detail.error as Error)?.message}</p>
      ) : null}

      {current?.status === 'done' && !report ? (
        <p className="err">
          گزارشِ این اجرا با نسخه‌ی این صفحه نمی‌خواند — کرالر و اپ از دو
          نسخه‌اند.
        </p>
      ) : null}

      {report ? (
        <>
          <p className={sum.status === 'ok' ? 'note' : 'err'}>
            {STATUS_ICON[sum.status]} {sum.note}
          </p>

          {/*
            تشخیص، درست زیرِ حکم — چون سؤالِ بعدیِ آدم همیشه «خب حالا چه
            کار کنم؟» است و جوابش تا امروز هیچ‌جا نوشته نمی‌شد.
          */}
          {(() => {
            const need = needOf(factsOf(report));
            if (!need) return null;
            return (
              <p className="note">
                <b>لازم دارد:</b> {need.label}
                {' '}<span className="dim">({NEED_KIND_LABEL[need.kind]})</span>
                <br />{need.why}
                <br /><b>قدمِ بعدی:</b> {need.todo}
              </p>
            );
          })()}

          <p className="dim" style={{ fontSize: 12, marginTop: 6 }}>
            با <span className="mono">{report.source.extractor ?? '—'}</span> ·
            {' '}tier <span className="mono">{report.source.tier ?? '—'}</span> ·
            {' '}منطقه <span className="mono">{report.source.timezone ?? '—'}</span>
            {report.source.aggregator ? ' · aggregator' : ''} ·
            {' '}{RUN_LABEL[current!.settings_from]}
          </p>

          <ul className="steps">
            {STEP_ORDER.map((key, i) => {
              const v = marks[i];
              const open = openStep === key;
              return (
                <li key={key} className={`step ${v?.status ?? 'none'}`}>
                  <button className="step-head" type="button"
                    onClick={() => setOpenStep(open ? null : key)}>
                    <span className="step-icon">{v ? STATUS_ICON[v.status] : '·'}</span>
                    <span className="step-name">{i + 1}. {STEP_LABEL[key]}</span>
                    <span className="step-note">{v ? v.note : 'به این قدم نرسید.'}</span>
                    <span className="dim">{open ? '▲' : '▼'}</span>
                  </button>

                  {open ? (
                    <div className="step-body">
                      {key === 'fetch' && steps.fetch ? (
                        <FetchBlock f={steps.fetch.page1} onOpen={openPageBy} />
                      ) : null}

                      {key === 'paginate' && steps.paginate ? (
                        steps.paginate.configured ? (
                          <>
                            <Facts rows={[
                              ['قاعده', <span className="mono">
                                {JSON.stringify(steps.paginate.rule)}</span>],
                              ['آیتمِ صفحه‌ی ۱', <span className="num">
                                {steps.paginate.items_page1 ?? '—'}</span>],
                              ['آیتمِ صفحه‌ی ۲', <span className="num">
                                {steps.paginate.items_page2 ?? '—'}</span>],
                              ['از آن‌ها تکراری', <span className="num">
                                {steps.paginate.repeated ?? '—'}</span>],
                              ['بدنه‌ها یکی‌اند؟', steps.paginate.same_body
                                ? <span className="bad">بله — صفحه جلو نرفته</span>
                                : 'نه'],
                              ['نشانه‌ی ادامه', steps.paginate.stop_marker_present == null
                                ? <span className="dim">قاعده‌اش تعریف نشده</span>
                                : (steps.paginate.stop_marker_present ? 'هست' : 'نیست')],
                            ]} />
                            {steps.paginate.page2 ? (
                              <div style={{ marginTop: 8 }}>
                                <FetchBlock f={steps.paginate.page2} onOpen={openPageBy} />
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <p className="dim">
                            برای این منبع <span className="mono">paginate</span> تعریف
                            نشده، پس فقط همان یک آدرس خوانده می‌شود.
                          </p>
                        )
                      ) : null}

                      {key === 'detect' && steps.detect ? (
                        <>
                          <Facts rows={[
                            ['یابنده', <span className="mono">{steps.detect.detector}</span>],
                            ['صفحه‌ی ۱', <span className="num">{steps.detect.page1_items}</span>],
                            ['صفحه‌ی ۲', <span className="num">
                              {steps.detect.page2_items ?? '—'}</span>],
                          ]} />
                          {steps.detect.samples.map((s) => <ItemBlock key={s.key} s={s} />)}
                          {steps.detect.samples_page2.length > 0 ? (
                            <>
                              <h3 style={{ marginTop: 10 }}>نمونه‌ی صفحه‌ی دوم</h3>
                              {steps.detect.samples_page2.map((s) => (
                                <ItemBlock key={s.key} s={s} />
                              ))}
                            </>
                          ) : null}
                        </>
                      ) : null}

                      {key === 'extract' && steps.extract ? (
                        <>
                          <Facts rows={[
                            ['استخراج‌کننده', <span className="mono">
                              {steps.extract.extractor}</span>],
                            ['آیتمِ فیلددار، صفحه‌ی ۱', <span className="num">
                              {steps.extract.page1_with_fields}</span>],
                            ['آیتمِ فیلددار، صفحه‌ی ۲', <span className="num">
                              {steps.extract.page2_with_fields ?? '—'}</span>],
                          ]} />
                          {steps.extract.samples.map((s, n) => (
                            <FieldBlock key={`${s.item_key}-${s.page}-${n}`} s={s} />
                          ))}
                          <Bakeoff step={steps.extract} busy={apply.isPending}
                            onApply={(name) => apply.mutate(name)} />
                        </>
                      ) : null}

                      {key === 'origin' && steps.origin ? (
                        <>
                          <Facts rows={[
                            ['goout_source', <span className="mono">
                              {steps.origin.goout_source ?? '—'}</span>],
                            ['aggregator', steps.origin.aggregator ? 'بله' : 'نه'],
                            ['انتخاب‌شده', steps.origin.chosen
                              ? <a className="mono" href={steps.origin.chosen}
                                  target="_blank" rel="noreferrer">{steps.origin.chosen}</a>
                              : <span className="dim">—</span>],
                          ]} />
                          {steps.origin.candidates.length > 0 ? (
                            <table className="tbl" style={{ marginTop: 8 }}>
                              <thead><tr><th>لینک</th><th>متنِ لینک</th><th className="num">امتیاز</th></tr></thead>
                              <tbody>
                                {steps.origin.candidates.map((c) => (
                                  <tr key={c.url}>
                                    <td className="mono">{c.url}</td>
                                    <td className="dim">{c.anchor ?? '—'}</td>
                                    <td className="num">{c.score.toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : <p className="dim">هیچ لینکِ بیرونی‌ای در آیتم نبود.</p>}
                          {steps.origin.fetched ? (
                            <div style={{ marginTop: 8 }}>
                              <FetchBlock f={steps.origin.fetched} onOpen={openPageBy} />
                            </div>
                          ) : null}
                        </>
                      ) : null}

                      {key === 'detail' && steps.detail ? (
                        steps.detail.url ? (
                          <>
                            {steps.detail.fetched ? (
                              <FetchBlock f={steps.detail.fetched} onOpen={openPageBy} />
                            ) : (
                              <p className="dim mono">{steps.detail.url}</p>
                            )}
                            {steps.detail.extra_fields.length > 0 ? (
                              <p className="dim" style={{ marginTop: 6 }}>
                                فیلدهایی که فقط این صفحه داد:{' '}
                                <span className="mono">
                                  {steps.detail.extra_fields.join('، ')}
                                </span>
                              </p>
                            ) : null}
                            {steps.detail.fields ? <FieldBlock s={steps.detail.fields} /> : null}
                          </>
                        ) : (
                          <p className="dim">{steps.detail.reason ?? '—'}</p>
                        )
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>

          {pages.length > 0 ? (
            <>
              <h3 style={{ marginTop: 12 }}>صفحه‌هایی که گرفته شد</h3>
              <div className="scrollx">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>نقش</th><th>آدرس</th><th className="num">کد</th>
                      <th className="num">حجم</th><th className="num">زمان</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {pages.map((p) => (
                      <tr key={p.id}>
                        <td>{PAGE_ROLE_LABEL[p.role]}</td>
                        <td className="mono">{p.url}</td>
                        <td className={`num${(p.http_status ?? 0) >= 400 ? ' bad' : ''}`}>
                          {p.http_status ?? '—'}
                        </td>
                        <td className="num">{humanBytes(p.bytes)}</td>
                        <td className="num dim">{p.duration_ms}ms</td>
                        <td>
                          {p.has_body ? (
                            <button className="linkbtn" type="button"
                              onClick={() => setOpenPage(p.id)}>دیدن</button>
                          ) : <span className="dim">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {openPage != null ? (
        <PageViewer id={openPage} onClose={() => setOpenPage(null)} />
      ) : null}
    </div>
  );
}
